// Offline outbox for site crews with no signal: writes that fail with a network error are saved on the
// phone (IndexedDB) and replayed, in order, as soon as a request gets through. Every replay carries the same
// X-Offline-Id so the server hands back the first result instead of clocking a crew in twice.
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DB_NAME = '365ops-offline';
const STORE = 'outbox';
const listeners = new Set();
let dbPromise = null;
let processing = false;
let offline = !navigator.onLine;
let lastSyncedCount = 0;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
};

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out && 'result' in out ? out.result : out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
};

export const listOutbox = async () => {
  const items = await tx('readonly', (s) => s.getAll());
  return (items || []).sort((a, b) => a.created_at.localeCompare(b.created_at));
};
const putItem = (item) => tx('readwrite', (s) => s.put(item));
export const removeItem = (id) => tx('readwrite', (s) => s.delete(id)).then(notify);

const notify = async () => {
  const items = await listOutbox().catch(() => []);
  const snap = { offline, items, pending: items.filter((i) => i.status === 'pending').length,
    failed: items.filter((i) => i.status === 'failed').length, processing, lastSyncedCount };
  listeners.forEach((fn) => fn(snap));
};
export const subscribe = (fn) => { listeners.add(fn); notify(); return () => listeners.delete(fn); };
export const isOffline = () => offline;

export const isNetworkError = (err) =>
  !!err && (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || err.message === 'Network Error' ||
    (err.name === 'TypeError' && /fetch|network|load failed/i.test(err.message || '')) ||
    (err.response?.status === 503 && err.response?.data?.offline === true) ||
    err.response?.headers?.['x-offline'] === '1');

const setOffline = (v) => { if (offline !== v) { offline = v; notify(); } };

// ── Job runners: one per thing a crew can do with no signal ──────────────────────────────
const hdr = (job, suffix = '') => ({ headers: { 'X-Offline-Id': `${job.id}${suffix}`, Authorization: `Bearer ${localStorage.getItem('token')}` } });

const RUNNERS = {
  // Daily PSI (create or edit) + its PDF. Crew clock-in is its own job so it never waits on the PDF.
  psi_submit: async (job) => {
    const p = job.payload;
    const r = p.inspectionId
      ? await axios.put(`${API}/inspections/${p.inspectionId}`, p.inspection, hdr(job))
      : await axios.post(`${API}/inspections/create`, p.inspection, hdr(job));
    const id = r.data?.inspection_id || p.inspectionId;
    if (id) await axios.post(`${API}/inspections/${id}/generate-pdf`, {}, hdr(job, ':pdf')).catch(() => null);
    return { inspection_id: id };
  },
  crew_clock_in: async (job) => (await axios.post(`${API}/shop/clock-in-crew`, job.payload, hdr(job))).data,
  crew_clock_out: async (job) => {
    const data = (await axios.post(`${API}/shop/clock-out-crew`, job.payload, hdr(job))).data;
    const explicit = (job.payload.employee_ids || []).length + (job.payload.contractor_ids || []).length;
    if (explicit && !(data?.clocked_out > 0)) {
      // 200 with nothing done = nobody was on the clock any more (a PM already clocked them out) — say so
      const err = new Error('No open clock-in found — already clocked out by someone else?');
      err.response = { status: 409, data: { detail: err.message } };
      throw err;
    }
    return data;
  },
  self_clock_in: async (job) => (await axios.post(`${API}/field/clock-in`, job.payload, hdr(job))).data,
  self_clock_out: async (job) => (await axios.post(`${API}/field/clock-out`, job.payload, hdr(job))).data,
};

// Try it now; if the network is the problem, keep it on the phone. Returns {queued, data}.
export const send = async (type, label, payload) => {
  const job = { id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`, type, label, payload,
    created_at: new Date().toISOString(), status: 'pending', attempts: 0, error: null,
    user: localStorage.getItem('token') ? JSON.parse(atob(localStorage.getItem('token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub : null };
  try {
    const data = await RUNNERS[type](job);
    setOffline(false);
    return { queued: false, data };
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    setOffline(true);
    await putItem(job);
    notify();
    return { queued: true, job };
  }
};

const reportFailure = async (job, error) => {
  try {
    await axios.post(`${API}/offline/sync-failed`, { job_id: job.id, job_type: job.type, label: job.label, error, created_at: job.created_at },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  } catch (e) { /* dispatch notice is best effort */ }
};

export const processOutbox = async () => {
  if (processing || !localStorage.getItem('token')) return;
  processing = true;
  notify();
  try {
    const items = (await listOutbox()).filter((i) => i.status === 'pending');
    for (const job of items) {
      try {
        const data = await RUNNERS[job.type](job);
        await removeItem(job.id);
        lastSyncedCount += 1;
        setOffline(false);
        window.dispatchEvent(new CustomEvent('offline-synced', { detail: { job, data } }));
      } catch (err) {
        if (isNetworkError(err)) { setOffline(true); break; } // still no signal — try again later
        const msg = err.response?.data?.detail || err.message || 'Rejected';
        await putItem({ ...job, status: 'failed', error: typeof msg === 'string' ? msg : JSON.stringify(msg), attempts: (job.attempts || 0) + 1, failed_at: new Date().toISOString() });
        setOffline(false);
        reportFailure(job, typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    }
  } finally {
    processing = false;
    notify();
  }
};

export const retryItem = async (id) => {
  const items = await listOutbox();
  const job = items.find((i) => i.id === id);
  if (!job) return;
  await putItem({ ...job, status: 'pending', error: null });
  await processOutbox();
};

// Pending clock jobs for the signed-in person — lets the clock screen show "saved on phone" state.
export const pendingOfType = async (types) => (await listOutbox()).filter((i) => types.includes(i.type) && i.status !== 'failed');

const ping = async () => {
  try {
    await axios.get(`${API}/offline/ping`, { timeout: 6000 });
    setOffline(false);
    return true;
  } catch (e) {
    if (isNetworkError(e)) setOffline(true);
    return false;
  }
};

// Reads the foreman will need with no signal — pulled once per online session so they're in the cache
// even if he never opened those screens today.
export const primeOfflineCache = async () => {
  const token = localStorage.getItem('token');
  if (!token || !navigator.serviceWorker?.controller) return;
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
  const urls = [
    `${API}/auth/me`, `${API}/inspections/templates/daily_psi`, `${API}/inspections/my-inspections?limit=25`,
    `${API}/field/clock-status`, `${API}/field/work-orders?q=`, `${API}/shop/field-crew-review?date=${today}`,
    `${API}/shop/work-orders`,
  ];
  navigator.serviceWorker.controller.postMessage({ type: 'PRIME', urls, headers: { Authorization: `Bearer ${token}` } });
};

let started = false;
export const startOfflineSync = () => {
  if (started) return;
  started = true;
  window.addEventListener('online', () => { setOffline(false); processOutbox(); });
  window.addEventListener('offline', () => setOffline(true));
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') processOutbox(); });
  setInterval(async () => {
    const items = await listOutbox().catch(() => []);
    if (items.some((i) => i.status === 'pending')) { if (await ping()) processOutbox(); }
  }, 45000);
  setTimeout(processOutbox, 1500);
  setTimeout(primeOfflineCache, 4000);
};
